import React, { useState } from "react";
import DSBox from "../../../system-design/DSBox";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";
import DSButton from "../../../system-design/DSButton";
import DSErrorButton from "../../../system-design/DSErrorButton";
import { DSBaseTable, DSTableCell } from "../../../system-design/Table";
import { Column } from "../../../system-design/Table/DSBaseTable/components/DSBaseTable";
import KlydosPickDialog from "../../../Gifts/GiftCategories/components/KlydosPickDialog";
import ConfirmationModal from "../../../ConfirmationModal";
import { useDevice } from "../../context/DeviceModalContext";
import useGetDeviceFavoriteKlydos, {
  DeviceFavoriteKlydo,
} from "../../hooks/useGetDeviceFavoriteKlydos";
import { hasSound, KlydoType } from "../../../../api/klydo";
import useGetKlydosByIds from "../../hooks/useGetKlydosByIds";
import KlydoModal from "../../../Klydos/components/KlydoModal";
import DSDialog from "../../../system-design/DSDialog";
import ImportDevicesFavoritesDialog from "../ImportDevicesFavoritesDialog";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSTooltip from "../../../system-design/DSTooltip";
import KlydoLoopUrl from "../../../../components/klydoPreview/KlydoLoopUrl";
import DSSectionHeading from "../../../system-design/DSSectionHeading";
import DSTypography from "../../../system-design/DSTypography";
import ReorderKlydosDialog from "../../../Klydos/components/ReorderKlydosDialog";
import { sectionCardSx } from "../../../system-design/sectionCardSx";

const DeviceFavoriteCard = () => {
  const { device, updateConfig, refetch } = useDevice();
  const [showFavoritesDialog, setShowFavoritesDialog] = useState(false);
  const [showResetConfirmationModal, setShowResetConfirmationModal] =
    useState(false);
  const [selectedKlydo, setSelectedKlydo] = useState<{ id: string } | null>(
    null,
  );
  const [openImportFavoritesDialog, setOpenImportFavoritesDialog] =
    useState(false);
  const [showReorderDialog, setShowReorderDialog] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [copiedIds, setCopiedIds] = useState(false);
  const [deletingKlydoId, setDeletingKlydoId] = useState<string | null>(null);
  const { fetchKlydosByIds } = useGetKlydosByIds();
  const favoriteIds: string[] = device.favorites?.value ?? [];
  // Mirrors the clock's own draft-visibility gate: dev = hideDev explicitly
  // false, plus the artist and rater flags.
  const draftsAllowed =
    device.hideDev?.value === false ||
    device.isArtist?.value === true ||
    device.isKlydosRater?.value === true;
  const { klydos, missingIds, isLoading, hasErrors } =
    useGetDeviceFavoriteKlydos(device.favorites?.value);

  const columns: Column<DeviceFavoriteKlydo>[] = [
    {
      field: "position",
      label: "#",
      size: 60,
      sortable: false,
    },
    {
      field: "loopUrl",
      label: "Klydo",
      sortable: false,
    },
    {
      field: "name",
      label: "Name",
      sortable: false,
    },
    {
      field: "chimesUrls",
      label: "Chime",
      sortable: false,
    },
    {
      field: "capabilitiesV2",
      label: "Sound",
      sortable: false,
    },
  ];

  const handleKlydoClick = (klydo: DeviceFavoriteKlydo) => {
    if (klydo.klydoId) {
      setSelectedKlydo({ id: klydo.klydoId });
    }
  };

  const rowBuilder = (klydo: DeviceFavoriteKlydo) => {
    return (
      <>
        <DSTableCell key="position" sx={{ width: 60, fontWeight: 600 }}>
          {klydo.position}
        </DSTableCell>
        <DSTableCell
          key="klydo"
          onClick={() => handleKlydoClick(klydo)}
          sx={{ cursor: "pointer" }}
        >
          <DSBox
            sx={{
              width: 35,
              height: 35,
              overflow: "hidden",
            }}
          >
            <KlydoLoopUrl loopUrl={klydo?.loopUrl || ""} />
          </DSBox>
        </DSTableCell>
        <DSTableCell key="author">
          {klydo?.name || klydo?.creator || "Unknown"}
        </DSTableCell>
        <DSTableCell key="chime">
          {klydo?.chimesUrls?.length ? "✓" : "✗"}
        </DSTableCell>
        <DSTableCell key="sound">
          {hasSound(klydo?.capabilitiesV2?.animationSound?.mode) ? "✓" : "✗"}
        </DSTableCell>
      </>
    );
  };

  const handleRemoveFavorite = async (klydo: DeviceFavoriteKlydo) => {
    setDeletingKlydoId(klydo.klydoId);
    const updatedFavorites =
      device.favorites?.value.filter((fav: string) => fav !== klydo.klydoId) ||
      [];

    try {
      await updateConfig("favorites", updatedFavorites);
      refetch();
    } finally {
      setDeletingKlydoId(null);
    }
  };

  const updateFavorites = async (newFavorites: string[]) => {
    const fetchedKlydos = await fetchKlydosByIds(newFavorites);
    const nonGreetFavorites = fetchedKlydos
      .filter((klydo) => !klydo.greet)
      .map((klydo) => klydo.id);
    await updateConfig("favorites", nonGreetFavorites);
    refetch();
  };

  // Only permutes the existing array, so the clock sees the same ids in a new
  // order — it rebuilds the favorites playlist in place (3s debounce) and keeps
  // playing the klydo it is on.
  const handleSaveOrder = async (orderedIds: string[]) => {
    setIsSavingOrder(true);
    try {
      await updateConfig("favorites", orderedIds);
      refetch();
      setShowReorderDialog(false);
    } finally {
      setIsSavingOrder(false);
    }
  };

  // The pick dialog accepts a pasted id list and keeps its order, so this is
  // how a second clock gets the exact same favorites in the exact same order.
  // JSON rather than a comma list: the dialog's parser needs a comma or a
  // newline to recognise a plain list, so a single-favorite copy would not
  // paste back.
  const handleCopyIds = async () => {
    await navigator.clipboard.writeText(JSON.stringify(favoriteIds));
    setCopiedIds(true);
    setTimeout(() => setCopiedIds(false), 2000);
  };

  const isImportDisabled = (device.favorites?.value?.length ?? 0) > 0;
  const tooltipTitle = isImportDisabled
    ? "You have favorites, please reset your favorites to import from other device. greets will not be imported."
    : "";

  return (
    <DSBox sx={{ mb: 2 }}>
      <DSCard sx={sectionCardSx}>
        <DSSectionHeading>
          {`Favorite Klydos ${device.favorites?.value && `(${device.favorites.value.length})`}`}
        </DSSectionHeading>

        <DSCardContent>
          <DSTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Rows are in playback order — the clock plays favorites top to
            bottom, starting from the one it last showed.
          </DSTypography>

          {missingIds.length > 0 && (
            <DSTypography
              variant="caption"
              color="error.main"
              sx={{ display: "block", mb: 1 }}
            >
              {`${missingIds.length} favorite(s) not found in the klydo DB and not listed below: ${missingIds.join(", ")}`}
            </DSTypography>
          )}

          {hasErrors ? (
            <DSBox
              sx={{ textAlign: "center", padding: "20px", color: "error.main" }}
            >
              Failed to load favorite klydos
            </DSBox>
          ) : isLoading ? (
            <DSBox sx={{ textAlign: "center", padding: "20px" }}>
              Loading favorite klydos...
            </DSBox>
          ) : device.favorites?.value && device.favorites.value.length > 0 ? (
            <DSBaseTable
              data={klydos}
              columns={columns}
              rowBuilder={rowBuilder}
              onPressDelete={handleRemoveFavorite}
              defaultRowsPerPage={5}
              deletingRowId={deletingKlydoId}
              getRowId={(klydo) => klydo.klydoId}
            />
          ) : (
            <DSBox sx={{ textAlign: "center", padding: "20px" }}>
              No favorites added yet
            </DSBox>
          )}

          <DSBox sx={{ display: "flex", gap: "10px", marginTop: "15px" }}>
            <DSButton onClick={() => setShowFavoritesDialog(true)}>
              Edit Favorites
            </DSButton>
            <DSButton
              disabled={favoriteIds.length < 2}
              onClick={() => setShowReorderDialog(true)}
            >
              Edit Order
            </DSButton>
            <DSTooltip
              slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}
              title="Copy the ids in this order — paste them into another device's Edit Favorites to clone the list AND its order."
            >
              <span>
                <DSButton
                  disabled={favoriteIds.length === 0}
                  onClick={handleCopyIds}
                >
                  {copiedIds ? "Copied!" : "Copy IDs"}
                </DSButton>
              </span>
            </DSTooltip>
            <DSTooltip
              slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}
              title={tooltipTitle}
            >
              <span>
                <DSPrimaryButton
                  disabled={isImportDisabled}
                  onClick={() => setOpenImportFavoritesDialog(true)}
                >
                  Import Other Device Favorites
                </DSPrimaryButton>
              </span>
            </DSTooltip>

            {device.favorites?.value && device.favorites.value.length > 0 && (
              <DSErrorButton
                onClick={() => setShowResetConfirmationModal(true)}
                sx={{ minWidth: "120px" }}
              >
                Reset Favorites
              </DSErrorButton>
            )}
          </DSBox>

          {showFavoritesDialog && (
            <KlydosPickDialog
              onClose={() => setShowFavoritesDialog(false)}
              selectedKlydos={device.favorites?.value || []}
              klydoType={draftsAllowed ? undefined : KlydoType.Sealed}
              onPickKlydos={(selectedKlydos) => {
                updateConfig("favorites", selectedKlydos).then(() => {
                  refetch();
                  setShowFavoritesDialog(false);
                });
              }}
            />
          )}

          <ConfirmationModal
            open={showResetConfirmationModal}
            message={`Are you sure you want to reset all favorites for this device? This action will remove ${device.favorites?.value?.length || 0} favorite(s) and cannot be undone.`}
            onClose={() => setShowResetConfirmationModal(false)}
            onConfirm={() => {
              updateConfig("favorites", []).then(() => {
                refetch();
                setShowResetConfirmationModal(false);
              });
            }}
          />

          {showReorderDialog && (
            <ReorderKlydosDialog
              title={`Favorites order — ${device.idf || device.id}`}
              description="The clock plays favorites in this order."
              klydosIds={favoriteIds}
              onSave={handleSaveOrder}
              onClose={() => setShowReorderDialog(false)}
              isSaving={isSavingOrder}
            />
          )}

          {openImportFavoritesDialog && (
            <DSDialog
              fullWidth
              maxWidth="sm"
              open={openImportFavoritesDialog}
              onClose={() => setOpenImportFavoritesDialog(false)}
            >
              <ImportDevicesFavoritesDialog
                onUpdateFavorites={updateFavorites}
                onClose={() => setOpenImportFavoritesDialog(false)}
              />
            </DSDialog>
          )}

          {selectedKlydo && (
            <DSDialog
              fullWidth
              maxWidth="lg"
              open={!!selectedKlydo}
              onClose={() => setSelectedKlydo(null)}
            >
              <KlydoModal
                klydoId={selectedKlydo.id}
                setShowModal={() => setSelectedKlydo(null)}
              />
            </DSDialog>
          )}
        </DSCardContent>
      </DSCard>
    </DSBox>
  );
};

export default DeviceFavoriteCard;
